/**
 * Thin async wrappers around existing stores/endpoints for the copilot
 * onboarding flow. No new business logic — each function adapts an existing
 * call. Functions marked "not yet wired" throw because there is no
 * client-side API to invoke MCP tools directly outside the chat/SSE
 * transport (see SseAgentTransport.js) — wire these once one exists.
 */
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchData from "Stores/deepResearchData";
import { settingsStoreData } from "Stores/settingsData";
import { userDetail } from "Stores/userDetail";
import { dataSearchState } from "Pages/Dashboard/dataSearchV2/state";
import customListState from "Pages/Dashboard/dataSearchV2/CustomList/state";

// Mirrors the default filter shape from dataSearchV2/state.js
// (createDefaultFilterComponentDataForImport, not exported there).
const createDefaultFilterComponentData = () => ({
  confidenceScore: [],
  excludeTeamOwns: true,
  industry: [],
  industryExclude: [],
  sector: [],
  employeeCount: [],
  revenue: [],
  department: [],
  funding: [],
  fundingTotalAmountStart: null,
  fundingTotalAmountEnd: null,
  fundingLastAmountStart: null,
  fundingLastAmountEnd: null,
  fundingDurationStart: null,
  fundingDurationEnd: null,
  employeesByDepartment: [],
  growthByDepartment: [],
  companyType: [],
  foundedYearStart: null,
  foundedYearEnd: null,
  technologies: [],
  level: [],
  title: [],
  titleExclude: [],
  skill: [],
  skillExclude: [],
  certification: [],
  certificationExclude: [],
  titleExactMatch: false,
  primaryActiveRoleOnly: true,
  lookalikeDomains: [],
  companyDomain: [],
  companyName: [],
  companyDomainList: [],
  companyNameList: [],
  companyLinkedIn: [],
  companyLinkedInList: [],
  companySearchType: "domain",
  companyKeywords: [],
  companyKeywordsExclude: [],
  contactKeywords: [],
  contactKeywordsExclude: [],
  profileBadge: [],
  country: [],
  state: [],
  city: [],
  companyCountry: [],
  companyState: [],
  companyCity: [],
  name: [],
  contactLinkedIn: [],
  contactSearchType: "name",
  excludeContactsMode: "list",
  excludeContactListId: null,
  excludeContactListName: "",
  excludeContactLinkedIn: []
});

// makeApi resolves (does not throw) on non-2xx, returning the axios error
// object, so success has to be asserted explicitly or a failed call reads as
// an empty-but-successful profile.
const isOk = res =>
  res && typeof res.status === "number" && res.status >= 200 && res.status < 300 && res.data;

export const enrichSeller = async website => {
  const res = await makeApi({
    url: URLS.deepResearchEnrichSeller,
    method: "POST",
    data: { website }
  });
  if (!isOk(res)) {
    const status = res?.status || res?.response?.status || "network";
    throw new Error(`enrichSeller failed (${status})`);
  }
  return res.data;
};

export const saveSeller = async profile => {
  return deepResearchData.saveSellerProfile(profile);
};

export const mapIcpToFilters = icpFilters => {
  const { title, level, industry, employeeCount, country } = icpFilters || {};
  return {
    ...createDefaultFilterComponentData(),
    title: title || [],
    level: level || [],
    industry: industry || [],
    employeeCount: employeeCount || [],
    country: country || [],
    excludeTeamOwns: true,
    primaryActiveRoleOnly: true
  };
};

export const runSearch = async icpFilters => {
  dataSearchState.setFilterComponentData(mapIcpToFilters(icpFilters));
  await dataSearchState.applyFiltersAndSearch();
  const { totalContacts = 0, contacts = [] } = dataSearchState.contactTableData || {};
  return { total: totalContacts, rows: contacts };
};

// Serves the prospect TABLE_REF shown in Step 4. Returns the rows already
// loaded by runSearch as a single page (the top matches), mapped to the
// standard prospect columns. It does not re-page the search, which would spend
// list-builder search credits; the chat states the full match count and the
// table shows the loaded sample, mirroring the discover page's own phrasing.
export const fetchProspectPage = async () => {
  const { contacts = [] } = dataSearchState.contactTableData || {};
  const rows = contacts.map(c => ({
    // Identity fields the add-to-list path needs (linkedIn is required to be
    // "addable"); the rest are for display.
    id: c.id || c.aiArkId || undefined,
    firstName: c.firstName || "",
    lastName: c.lastName || "",
    linkedIn: c.linkedIn || c.linkedin || c.link || "",
    domain: c.companyDomain || c.primaryDomain || c.companyWebsite || "",
    companyLinkedIn: c.companyLinkedIn || undefined,
    city: c.city || "",
    state: c.state || "",
    country: c.country || "",
    name: [c.firstName, c.lastName].filter(Boolean).join(" ") || c.name || "",
    title: c.title || "",
    company: c.companyName || c.company || "",
    industry: c.companyIndustry || c.industry || "",
    location: [c.city, c.state, c.country].filter(Boolean).join(", ")
  }));
  return { rows, totalCount: rows.length, page: 1, pageSize: rows.length || 1 };
};

// Adds specific selected prospect rows (from the popup table) to a list, so a
// user who hand-picks contacts and clicks "Add to list" gets exactly those.
// Mirrors dataSearchV2's buildAddToDirectoryPayload shape. Returns {added, skipped}.
// The popup add-to-list path only carries the list id, so the handoff had no
// name to hand the sequence builder. The picker has already loaded the lists.
export const getListName = listId => {
  if (!listId) return "";
  const all = customListState.totalContactList || [];
  const hit = all.find(l => String(l.id || l._id) === String(listId));
  return (hit && (hit.name || hit.listName)) || "";
};

export const addRowsToList = async (listId, rows) => {
  const stripHttp = u => String(u || "").replace(/^https?:\/\//i, "").trim();
  const mapped = (rows || [])
    .map(r => {
      const li = stripHttp(r.linkedIn || r.linkedin || r.link);
      if (!li) return null;
      return {
        firstName: r.firstName || null,
        lastName: r.lastName || null,
        company: r.company || null,
        domain: r.domain ? String(r.domain).trim() : null,
        jobTitle: r.title || null,
        industry: r.industry || null,
        linkedinId: li,
        email: null,
        city: r.city || null,
        state: r.state || null,
        country: r.country || null,
        liCompanyId: r.companyLinkedIn || null,
        aiArkId: r.id || null
      };
    })
    .filter(Boolean);
  const skipped = (rows || []).length - mapped.length;
  if (!mapped.length) return { added: 0, skipped };
  const res = await makeApi({
    url: URLS.addContactsToDirectory,
    method: "POST",
    data: { contacts: mapped, contactTags: [], listId: listId ? String(listId) : null, useSnCredits: true }
  });
  if (!isOk(res)) return { added: 0, skipped: (rows || []).length, error: true };
  const data = res.data || {};
  const added = (data.validContacts != null ? data.validContacts : mapped.length);
  return { added, skipped };
};

export const loosenSearch = async icpFilters => {
  const loosened = { ...icpFilters };
  let dropped = null;
  if (loosened.industry?.length) {
    loosened.industry = [];
    dropped = "industry";
  } else if (loosened.level?.length) {
    loosened.level = [];
    dropped = "level";
  }
  const { total, rows } = await runSearch(loosened);
  return { total, rows, dropped };
};

// Save the matched prospects to a real list. addContactToList adds either the
// current selection or, for a bulk/range selection, up to `limit` from the
// search. Onboarding never touches the checkboxes, so drive the bulk path with
// an explicit cap (respecting the plan's list-save limit the backend enforces).
const LIST_SAVE_CAP = 1000;

export const saveList = async (name, filterComponentData, total) => {
  const list = await customListState.createContactList(name);
  if (!list) return { listId: "", listName: name, listSize: 0 };
  const cap = Math.min(total || LIST_SAVE_CAP, LIST_SAVE_CAP) || LIST_SAVE_CAP;
  try {
    dataSearchState.rangeSelected = { type: "bulk", limit: cap };
    await dataSearchState.addContactToList(list);
  } catch (e) {
    /* best-effort: the list exists even if the bulk add hiccups */
  }
  return {
    listId: list.id || list._id || list.listId || "",
    listName: list.listName || name,
    listSize: cap
  };
};

export const getCredits = async () => {
  // TODO(onboarding): wire to MCP get_credits — no client-side MCP tool
  // invocation exists outside the chat/SSE transport. Falling back to the
  // settings store's known remaining credit total.
  return {
    remaining: settingsStoreData.totalRemainingCredits || 0,
    email: 1,
    phone: 10
  };
};

export const enrichContacts = async (type, listId) => {
  const purchaseType = type === "EMAIL_PHONE" ? { emailPurchase: true, phonePurchase: true } : { emailPurchase: true };
  try {
    await dataSearchState.purchaseBulkContact(purchaseType);
    return { ok: true, message: "Enrichment started" };
  } catch (err) {
    return { ok: false, message: err?.message || "Enrichment failed" };
  }
};

export const connectStatus = () => {
  return {
    email: (userDetail.connectedInboxes?.length || 0) > 0,
    linkedIn: (userDetail.activeLinkedInAccounts?.length || 0) > 0
  };
};

// Creates a real DRAFT sequence via the native web endpoint (same POST /cm/seq
// the sequence page uses). The draft starts with no steps: onboarding never
// sends, and step copy + contact enrolment are done on the sequence detail
// page, where the product's own AI writer and enrolment live. We return the
// real step count from the response (0 for a fresh draft) rather than a
// hardcoded promise, so the handoff cannot claim steps that do not exist.
const createSequenceDraft = async name => {
  const payload = {
    accessType: "READ_WRITE",
    bulk: false,
    connectedEmail: null,
    currentStage: null,
    folderId: null,
    folderName: "My Sequences",
    shareType: "PRIVATE",
    stages: {},
    type: "New Sequence",
    name
  };
  const res = await makeApi({ url: URLS.createSequence, method: "POST", data: payload });
  if (!isOk(res) || !res.data?.id) {
    const status = res?.status || res?.response?.status || "network";
    throw new Error(`createSequence failed (${status})`);
  }
  const seq = res.data;
  const stepCount = Array.isArray(seq.stages)
    ? seq.stages.length
    : seq.stages && typeof seq.stages === "object"
      ? Object.keys(seq.stages).length
      : 0;
  return { sequenceId: seq.id, steps: stepCount };
};

export const createEmailSequence = async name => {
  const { sequenceId, steps } = await createSequenceDraft(name);
  return { sequenceId, steps };
};

export const createDeepResearchSequence = async name => {
  const { sequenceId, steps } = await createSequenceDraft(name);
  return { sequenceId, steps, deepResearch: true };
};

// Seeds the editable ICP card from the enriched seller profile. There is no
// structured ICP on the profile (titles/industries/size/geo live only as
// list-builder filters), so this is a light client heuristic; the user edits
// the card before the Step 4 search runs, per the spec.
export const deriveIcp = async profile => {
  const p = profile || {};
  const industry = []
    .concat(p.segments || [], p.industries || [])
    .filter(Boolean)
    .slice(0, 5);
  const country = p.location ? [String(p.location).split(",").pop().trim()].filter(Boolean) : [];
  return { title: [], level: [], industry, employeeCount: [], country };
};
